// Centralized WhatsApp deep-link builder for "Haus des Handys".
//
// Strategy: open WhatsApp directly via the `whatsapp://` custom protocol
// (handled natively by the WhatsApp Desktop app, iOS app and Android app).
// This bypasses the api.whatsapp.com "Continue to Chat" intermediate page
// completely.
//
// Each builder returns `{ href, onClick }`:
//   - `href`  → `https://wa.me/<number>?text=…`  (universal web fallback,
//                used for hover preview / right-click copy / SEO / users
//                without WhatsApp installed)
//   - `onClick` → calls window.location.href = `whatsapp://send?phone=…`
//                via a click handler that suppresses the anchor navigation
//                so the user never sees an intermediate page.
//
export const WA_NUMBER = "436764440304"; // +43 676 4440304

const buildHref = (text) =>
  `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;

const handleClick = (text) => (e) => {
  e.preventDefault();
  const encoded = encodeURIComponent(text);
  // Custom protocol → handled by the OS-registered WhatsApp app.
  // Triggers a permission prompt on the very first click in desktop
  // browsers, opens the chat directly thereafter.
  window.location.href = `whatsapp://send?phone=${WA_NUMBER}&text=${encoded}`;
};

const make = (text) => ({ href: buildHref(text), onClick: handleClick(text), text });

export const whatsappLinks = {
  generic: () =>
    make("Hallo Haus des Handys, ich habe Ihre Website gesehen und habe eine Frage."),

  repair: ({ brand, model, issue, price, customerName }) => {
    const lines = [
      "Hallo Haus des Handys,",
      "",
      `ich möchte mein ${[brand, model].filter(Boolean).join(" ")} reparieren lassen.`,
      `Problem: ${issue}`,
    ];
    if (price) lines.push(`Geschätzter Preis: ab ${price} €`);
    if (customerName) lines.push("", `Mein Name: ${customerName}`);
    lines.push("", "Wann hättet ihr Zeit?");
    return make(lines.join("\n"));
  },

  product: ({ name, price, compatible, sku }) => {
    const lines = [
      "Hallo Haus des Handys,",
      "",
      "ich interessiere mich für:",
      `${name} — € ${Number(price).toFixed(2)}`,
    ];
    if (compatible) lines.push(`Für: ${compatible}`);
    if (sku) lines.push(`SKU: ${sku}`);
    lines.push("", "Habt ihr das auf Lager?");
    return make(lines.join("\n"));
  },

  preorder: ({ model, color, storage }) => {
    const lines = [
      "Hallo Haus des Handys,",
      "",
      `ich interessiere mich für das ${model}.`,
    ];
    if (color) lines.push(`Farbe: ${color}`);
    if (storage) lines.push(`Speicher: ${storage}`);
    lines.push("", "Bitte um Info zu Verfügbarkeit und Finanzierung (Raten).");
    return make(lines.join("\n"));
  },

  refurbished: ({ model, storage, price }) => {
    const lines = [
      "Hallo Haus des Handys,",
      "",
      "ich interessiere mich für ein refurbished iPhone:",
      `Modell: ${model}`,
    ];
    if (storage) lines.push(`Speicher: ${storage}`);
    if (price) lines.push(`Richtpreis: ca. € ${price}`);
    lines.push("", "Habt ihr das aktuell verfügbar?");
    return make(lines.join("\n"));
  },

  cart: (items, total) => {
    const lines = [
      "Hallo Haus des Handys,",
      "",
      "ich möchte folgende Artikel bestellen:",
      ...items.map((i) => `- ${i.qty}x ${i.name} — € ${(i.price * i.qty).toFixed(2)}`),
      "",
      `Gesamt: € ${Number(total).toFixed(2)}`,
    ];
    return make(lines.join("\n"));
  },

  order: ({ orderId, items, total }) => {
    const lines = [
      "Hallo Haus des Handys,",
      "",
      `Bestellung #${orderId} aufgegeben:`,
      ...items.map((i) => `- ${i.qty}x ${i.name} — € ${(i.price * i.qty).toFixed(2)}`),
      "",
      `Gesamt: € ${Number(total).toFixed(2)}`,
      "",
      "Bitte um Bestätigung. Danke!",
    ];
    return make(lines.join("\n"));
  },
};
